import * as React from 'react'
import { useTenantContext } from '@/components/tenant/tenant-context'
import type { DestinationId } from '@/components/visitone/navigation-model'
import {
  can,
  canChange,
  canOpenSetupHome,
  canView,
  hasFeature,
  landing,
  pageStatus,
  permittedDestinations,
  railDestinations,
} from '@/lib/access'

/*
 * The current tenant's access decision (VIS-329), with the pure helpers in
 * `@/lib/access` bound to it. Only used inside the shell, which renders once
 * the decision has loaded.
 */
export function useAccess() {
  const { access, currentTenant } = useTenantContext()
  return React.useMemo(
    () => ({
      access,
      /** The current tenant's name, for the `{tenant}` in access copy. */
      tenantName: currentTenant?.name ?? '',
      destinations: permittedDestinations(access),
      /** Permitted destinations, plus Setup when Landlords is held without that destination. */
      railDestinations: railDestinations(access),
      landing: landing(access),
      canView: (destination: DestinationId) => canView(access, destination),
      canChange: (destination: DestinationId) => canChange(access, destination),
      can: (action: string) => can(access, action),
      /** Setup's index opens (the setup destination, or Landlords held without it). */
      canOpenSetupHome: canOpenSetupHome(access),
      hasFeature: (feature: string) => hasFeature(access, feature),
      pageStatus: (destination: DestinationId, feature?: string) => pageStatus(access, destination, feature),
    }),
    [access, currentTenant],
  )
}

export type AccessHelpers = ReturnType<typeof useAccess>
